// Phase 9.2 — Manual PO create. Creates a blank draft, then redirects
// to the draft-review page so the user can add line items etc.
//
// Phase 9.7.1b — Pan India format now supports MULTI-OUTLET selection:
//   • Location-wise    → single-destination dropdown (as before)
//   • Pan India        → multi-select outlet grid + Select all / Deselect all

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function FnbPoManualCreate() {
  const navigate = useNavigate();
  const [format, setFormat] = useState("location_wise");
  const [vendorId, setVendorId] = useState("");
  const [destination, setDestination] = useState("");      // location_wise
  const [multiDest, setMultiDest] = useState([]);          // pan_india
  const [outletFilter, setOutletFilter] = useState("");
  const [notes, setNotes] = useState("");
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/fnb/vendors"),
      api.get("/fnb/locations"),
    ]).then(([v, l]) => {
      setVendors(v.data);
      // Phase 9.7.2 — destination universe is every ACTIVE outlet.
      // `is_po_target` is preserved on the schema (see backfill script)
      // but no longer the UI gate — a location that was never in the
      // legacy 11 should still be reachable via Pan-India.
      const active = l.data.filter(
        (x) => x.active !== false && x.is_active !== false && !x.is_warehouse,
      );
      setLocations(active);
      // Default: pre-select all active outlets for pan-india use
      setMultiDest(active.map((x) => x.location_id));
    }).catch(() => {});
  }, []);

  // Filter the visible grid by the search box. Selection state is kept
  // for outlets not visible under the current filter — they stay ticked.
  const visibleLocations = useMemo(() => {
    if (!outletFilter) return locations;
    const f = outletFilter.toLowerCase();
    return locations.filter(
      (l) =>
        (l.display_name || "").toLowerCase().includes(f)
        || l.location_id.toLowerCase().includes(f),
    );
  }, [locations, outletFilter]);

  const toggleMulti = (locId) => {
    setMultiDest((prev) =>
      prev.includes(locId) ? prev.filter((x) => x !== locId) : [...prev, locId],
    );
  };
  const selectAll = () => setMultiDest(locations.map((l) => l.location_id));
  const deselectAll = () => setMultiDest([]);

  // Cross-format persistence: when user switches Pan India → Location-wise,
  // keep the first selected outlet as the single destination.
  const setFormatAndReconcile = (fmt) => {
    if (fmt === "location_wise" && !destination) {
      if (multiDest.length > 0) setDestination(multiDest[0]);
      else if (locations.length > 0) setDestination(locations[0].location_id);
    }
    if (fmt === "pan_india" && destination && !multiDest.includes(destination)) {
      setMultiDest((prev) => (prev.length > 0 ? prev : [destination]));
    }
    setFormat(fmt);
  };

  const destsForSubmit =
    format === "pan_india" ? multiDest : (destination ? [destination] : []);

  const create = async () => {
    if (!vendorId) {
      toast.error("Vendor is required");
      return;
    }
    if (destsForSubmit.length === 0) {
      toast.error(
        format === "pan_india"
          ? "Pick at least one destination outlet"
          : "Destination location is required",
      );
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/fnb/pos", {
        format,
        vendor_id: vendorId,
        destination_locations: destsForSubmit,
        ship_to_location_id:
          format === "location_wise" ? destsForSubmit[0] : null,
        notes,
        line_items: [],
        // Phase 9.7.6 — starting-full flow. Backend auto-populates
        // every SKU linked to the vendor as a draft line so the
        // operator can then Remove what they don't want.
        auto_populate_vendor_lines: true,
      });
      toast.success(`${res.data.po_number} created — add line items below`);
      navigate(`/tools/fnb-po/pos/${res.data.po_id}`);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fnb-po-manual-create">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Manual PO</h1>
        <p className="text-sm text-muted-foreground">
          Blank draft — add line items after picking vendor + destination.
        </p>
      </header>

      <Card className={format === "pan_india" ? "max-w-3xl" : "max-w-xl"}>
        <CardHeader><div className="text-sm font-medium">Basics</div></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Format</label>
            <div className="flex gap-2 mt-1">
              <button
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  format === "pan_india" ? "bg-foreground text-background" : "border-border/70 hover:bg-muted"
                }`}
                onClick={() => setFormatAndReconcile("pan_india")}
                data-testid="manual-fmt-pan-india"
              >
                Pan India
              </button>
              <button
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  format === "location_wise" ? "bg-foreground text-background" : "border-border/70 hover:bg-muted"
                }`}
                onClick={() => setFormatAndReconcile("location_wise")}
                data-testid="manual-fmt-location-wise"
              >
                Location-wise
              </button>
            </div>
            <p
              className="text-xs text-muted-foreground mt-1.5"
              data-testid="manual-format-caption"
            >
              {format === "pan_india"
                ? "Pan India creates a single PO covering all selected outlets (matrix format)."
                : "Location-wise creates one PO per outlet."}
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Vendor</label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger className="text-sm" data-testid="manual-vendor">
                <SelectValue placeholder="Pick a vendor…" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.vendor_id} value={v.vendor_id} className="text-sm">
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {format === "location_wise" ? (
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Destination
              </label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger className="text-sm" data-testid="manual-destination">
                  <SelectValue placeholder="Pick a destination location…" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id} className="text-sm">
                      {l.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2" data-testid="manual-multi-destination">
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Destinations
                </label>
                <Badge
                  variant={multiDest.length > 0 ? "default" : "outline"}
                  className="text-[11px]"
                  data-testid="manual-multi-count"
                >
                  {multiDest.length} selected
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={outletFilter}
                  onChange={(e) => setOutletFilter(e.target.value)}
                  placeholder="Filter outlets…"
                  className="h-8 text-xs max-w-xs"
                  data-testid="manual-multi-filter"
                />
                <Button
                  variant="ghost" size="sm"
                  onClick={selectAll}
                  className="text-xs"
                  data-testid="manual-multi-select-all"
                >
                  Select all
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={deselectAll}
                  className="text-xs"
                  data-testid="manual-multi-deselect-all"
                >
                  Deselect all
                </Button>
              </div>
              <div
                className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-72 overflow-auto rounded-md border border-border/60 p-2"
                data-testid="manual-multi-grid"
              >
                {visibleLocations.length === 0 ? (
                  <div className="col-span-full text-center text-xs text-muted-foreground py-6">
                    No outlets match &ldquo;{outletFilter}&rdquo;
                  </div>
                ) : (
                  visibleLocations.map((l) => {
                    const checked = multiDest.includes(l.location_id);
                    return (
                      <label
                        key={l.location_id}
                        className={
                          "flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer text-xs " +
                          (checked
                            ? "bg-foreground/5 border border-foreground/20"
                            : "border border-transparent hover:bg-muted/40")
                        }
                        data-testid={`manual-outlet-${l.location_id}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleMulti(l.location_id)}
                          data-testid={`manual-outlet-cb-${l.location_id}`}
                        />
                        <span className="truncate">{l.display_name}</span>
                      </label>
                    );
                  })
                )}
              </div>
              <p
                className="text-[11px] text-muted-foreground"
                data-testid="manual-multi-caption"
              >
                Showing all active outlets. Manage active/inactive on the{" "}
                <a
                  href="/tools/fnb-po/locations"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Locations page
                </a>
                .
              </p>
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="text-xs mt-1"
              rows={3}
              placeholder="Internal notes…"
              data-testid="manual-notes"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={create}
              disabled={
                creating
                || !vendorId
                || destsForSubmit.length === 0
              }
              data-testid="manual-create-btn"
            >
              {creating ? "Creating…" : "Create blank draft →"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
